import { OidcUserInfo, useOidcUser } from '@axa-fr/react-oidc';
import React, { ReactElement, ReactNode, useContext } from 'react';
import { AvailableRole, Product, Role } from './Roles';

export type OidcUserInfoWithRole = OidcUserInfo & {
  role?: string[] | undefined;
  amr?: boolean | undefined;
};

interface UserProviderContextProps {
  oidcUser: OidcUserInfoWithRole;
  isInRole: (product: Product, roles: Role | Role[]) => boolean;
  getUserRolesForDomain(product: Product): Role[];
  hasRole: (roleOrRoles: AvailableRole | AvailableRole[]) => boolean;
}

const UserContext = React.createContext<UserProviderContextProps>({
  oidcUser: { sub: '' },
  isInRole: (product: Product, roles: Role | Role[]) => false,
  getUserRolesForDomain: (product: Product) => [],
  hasRole: (roleOrRoles: AvailableRole | AvailableRole[]) => false
});

export function UserProviderContext({ children }: { children: ReactNode }): ReactElement {
  const { oidcUser, oidcUserLoadingState } = useOidcUser('config_show_access_token');

  function isInRole(product: Product, roleOrRoles: Role | Role[]): boolean {
    const roles = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
    const userWithRole = oidcUser as OidcUserInfoWithRole;

    if (!userWithRole.role?.length) {
      return false;
    }
    const rolesToCheck = new Set(roles);

    return userWithRole.role.some(entry => {
      const colonIndex = entry.indexOf(':');
      if (colonIndex === -1) return false; // Invalid format

      const entryDomain = entry.slice(0, colonIndex);
      const entryRole = entry.slice(colonIndex + 1);

      return entryDomain === product && rolesToCheck.has(entryRole as Role);
    });
  }

  function hasRole(roleOrRoles: AvailableRole | AvailableRole[]): boolean {
    const roles = Array.isArray(roleOrRoles) ? roleOrRoles : [roleOrRoles];
    const userWithRole = oidcUser as OidcUserInfoWithRole;

    if (!userWithRole.role?.length) {
      return false;
    }

    const rolesToCheck = new Set(roles);
    return userWithRole.role.some(role => rolesToCheck.has(role as AvailableRole));
  }

  function getUserRolesForDomain(product: Product): Role[] {
    const userWithRole = oidcUser as OidcUserInfoWithRole;

    if (!userWithRole.role?.length) {
      return [];
    }

    return userWithRole.role
      .filter(entry => entry.startsWith(`${product}:`))
      .map(entry => entry.split(':')[1] as Role);
  }

  const value = {
    oidcUser,
    hasRole,
    isInRole,
    getUserRolesForDomain
  };

  return oidcUserLoadingState == 'User loaded' ? (
    <UserContext.Provider value={value}>{children}</UserContext.Provider>
  ) : (
    <></>
  );
}

export function useUserContext() {
  return useContext(UserContext);
}
